import {Injectable} from '@angular/core';
import {HttpService} from '@app/core/services/http.service';
import {AuthService} from '@app/core/services/auth.service';
import {User} from '@app/core/models/user.model';
import {Tenant} from '@app/core/models/tenant.model';
import {Department} from '@app/core/models/department.model';
import {BaseService} from '@app/core/services/base.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {EINTEGRATIONSMODEL} from '@app/core/enums';

export interface ITenantPostPayload {
	company_name: string;
	vat_number: string;
	name: string;
	phone_number: number;
	email: string;
	language_id: number;
	hub_token?: string;
}

export interface ITenantPatchPayload {
	company_name?: string;
	vat_number?: number;
	address?: string;
	city?: string;
	postal_code?: string;
	email?: string;
	phone_ext?: string;
	phone_number?: number;
	operational_start_time?: string;
	payment_period?: number;
}

@Injectable()
export class TenantService extends BaseService {
	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public fresh(params?): Tenant {
		if (!params) {
			return new Tenant(this.tenantTZ, this.interService);
		}

		return new Tenant(this.tenantTZ, this.interService).deserialize(params);
	}

	public get(): Observable<Tenant> {
		return this.httpService.getJson<Tenant>('tenants/me').pipe(
			map((res: any) => this.fresh(res))
		);
	}

	public register(payload: ITenantPostPayload): Observable<Tenant> {
		return this.httpService.postExternalUrlJson(this.httpService.shortUrl + 'frontpage/signUp', payload) // this.httpService.shortUrl 'https://api.rostlog.dk/'
			.pipe(map(t => this.fresh(t)));
	}

	public patch(payload: ITenantPatchPayload): Observable<Tenant> {
		return this.httpService.patchJson('tenants/me', payload).pipe(map((res) => this.fresh(res)));
	}

	public getDatabaseSettingsWithUsers(): Observable<Tenant> {
		return this.httpService.getJson<Tenant>('databaseSettings/withUsers').pipe(map((res: Tenant) => {
			res = new Tenant(this.tenantTZ, this.interService).deserialize(res);
			res.users = res.users.map(user => new User(this.tenantTZ, this.interService).deserialize(user));

			return res;
		}));
	}

	public getDatabaseSettingsWithDepartments(): Observable<Tenant> {
		return this.httpService.getJson<Tenant>('databaseSettings/withDepartments').pipe(map((res: Tenant) => {
			res = new Tenant(this.tenantTZ, this.interService).deserialize(res);
			res.departments = res.departments.map(department => new Department(this.tenantTZ, this.interService).deserialize(department));

			return res;
		}));
	}

	public patchDatabaseSettings(payload: {}): Observable<any> {
		return this.httpService.patchJson<{ success: boolean, error: {} }>('databaseSettings', payload);
	}

	public saveIntegrations(paymentNumberModels: User[] | Department[] = [], paymentNumberModel: EINTEGRATIONSMODEL): Observable<any> {
		const url = (paymentNumberModel === EINTEGRATIONSMODEL.USERS)
			? 'departments/' + this.authService.selectedDepartment.id + '/updatePaymentNumber'
			: 'departments/updatePaymentNumber';
		// @ts-ignore
		const data = paymentNumberModels.map((model: Department | User) =>
			({
				id: model.id,
				payment_number: model[model.getPaymentNumber()],
			}));

		return this.httpService.patchJson<any>(url, data);
	}
}
